import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Status } from "../../common/constants";
import {
  IAddTemplatePayload,
  ITemplate,
  emptyTemplateForm,
  templateToForm,
} from "../../common/types/templateTypes";
import useNotification from "../../components/notification/useNotification";
import { useSpinner } from "../../customHooks";
import {
  addTemplate,
  getTemplates,
  selector as templatesSelector,
  updateTemplate,
} from "../../components/superAdmin/templatesSlice";

export const useManageTemplates = () => {
  const dispatch = useDispatch<any>();
  const { templates, status, addStatus, updateStatus, error } =
    useSelector(templatesSelector);
  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const [search, setSearch] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "public" | "private">("all");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [form, setForm] = useState<IAddTemplatePayload>(emptyTemplateForm);

  const spinnerStatus =
    status === Status.PENDING ||
    addStatus === Status.PENDING ||
    updateStatus === Status.PENDING
      ? Status.PENDING
      : Status.IDLE;
  useSpinner(spinnerStatus);

  useEffect(() => {
    dispatch(getTemplates());
  }, [dispatch]);

  const filteredTemplates = useMemo(
    () =>
      templates.filter((template) => {
        if (visibilityFilter === "public" && !template.public) return false;
        if (visibilityFilter === "private" && template.public) return false;
        return `${template.name} ${template.templateId}`
          .toLowerCase()
          .includes(search.toLowerCase());
      }),
    [templates, search, visibilityFilter],
  );

  const resetForm = () => setForm(emptyTemplateForm);

  const handleAddClick = () => {
    setModalMode("add");
    resetForm();
    setShowModal(true);
  };

  const handleEditClick = (template: ITemplate) => {
    setModalMode("edit");
    setForm(templateToForm(template));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = useCallback(async () => {
    if (!form.name?.trim()) {
      showWarningNotification("Template name is required");
      return;
    }
    if (modalMode === "add" && !form.templateId) {
      showWarningNotification("Template ID is required");
      return;
    }
    if (modalMode === "add" && !form.file) {
      showWarningNotification("Template image (file) is required");
      return;
    }

    if (modalMode === "add") {
      const result = await dispatch(addTemplate(form));
      if (addTemplate.fulfilled.match(result)) {
        showSuccessNotification("Template added");
        handleCloseModal();
        await dispatch(getTemplates());
        return;
      }
      showWarningNotification(result.payload || error || "Failed to add template");
      return;
    }

    if (!form.templateId) {
      showWarningNotification("templateId is required");
      return;
    }
    const result = await dispatch(
      updateTemplate({ ...form, templateId: form.templateId }),
    );
    if (updateTemplate.fulfilled.match(result)) {
      showSuccessNotification("Template updated");
      handleCloseModal();
      await dispatch(getTemplates());
      return;
    }
    showWarningNotification(result.payload || error || "Failed to update template");
  }, [dispatch, error, form, modalMode, showSuccessNotification, showWarningNotification]);

  return {
    templates: filteredTemplates,
    search,
    setSearch,
    visibilityFilter,
    setVisibilityFilter,
    showModal,
    modalMode,
    form,
    setForm,
    handleAddClick,
    handleEditClick,
    handleCloseModal,
    handleSubmit,
    isBusy:
      status === Status.PENDING ||
      addStatus === Status.PENDING ||
      updateStatus === Status.PENDING,
  };
};
